import api from './api';

// PIN gate is currently disabled — token helpers and verifyPin kept for easy re-enable.
const BYPASS_TOKEN_KEY = 'bypassSessionToken';
const BYPASS_TOKEN_EXP_KEY = 'bypassSessionExp';

export interface BypassMetadata {
  orderId?: string;
  patientName?: string;
  patientAge?: number;
  patientGender?: 'male' | 'female' | 'other';
  testDate?: string;
  labName: string;
}

export interface PreviewRow {
  rowIndex: number;
  lineNumber: number;
  parameterName: string;
  value: string;
  unit: string;
  referenceRange: string;
  flag: string | null;
  category: string | null;
  method: string | null;
  comments: string | null;
  match: null | {
    tier: 1 | 2 | 3 | 4;
    source: 'vendor' | 'master';
    standardizedName: string;
    masterId?: string;
    parameterId?: string;
    vendorRowId?: string;
    category?: string;
  };
  suggestions: Array<{
    source: 'vendor' | 'master';
    standardizedName: string;
    masterId?: string;
    parameterId?: string;
    vendorRowId?: string;
    score: number;
  }>;
}

export interface PreviewResponse {
  vendorName: string;
  existingReport: null | { id: string; status: string; action: 'patch' | 'overrideRequiresPin' };
  summary: { totalRows: number; matched: number; needsDecision: number };
  rows: PreviewRow[];
}

export interface RowDecision {
  action: 'map' | 'addNew';
  masterId?: string;
  vendorRowId?: string;
  standardizedName?: string;
  newParam?: {
    standardizedName: string;
    unit: string | null;
    category: string | null;
    valueType: 'numeric' | 'text' | 'alphanumeric' | 'range';
  };
}

function setBypassToken(token: string, expiresAt: string) {
  localStorage.setItem(BYPASS_TOKEN_KEY, token);
  localStorage.setItem(BYPASS_TOKEN_EXP_KEY, expiresAt);
}

export function getBypassToken(): string | null {
  const token = localStorage.getItem(BYPASS_TOKEN_KEY);
  const exp = localStorage.getItem(BYPASS_TOKEN_EXP_KEY);
  if (!token || !exp) return null;
  if (new Date(exp).getTime() <= Date.now()) {
    clearBypassToken();
    return null;
  }
  return token;
}

export function clearBypassToken() {
  localStorage.removeItem(BYPASS_TOKEN_KEY);
  localStorage.removeItem(BYPASS_TOKEN_EXP_KEY);
}

export async function verifyPin(pin: string): Promise<{ token: string; expiresAt: string; ttlMinutes: number }> {
  const res = await api.post('/bypass/pin/verify', { pin });
  setBypassToken(res.data.token, res.data.expiresAt);
  return res.data;
}

export async function previewBypass(metadata: BypassMetadata, csvFile: File): Promise<PreviewResponse> {
  const fd = new FormData();
  fd.append('metadata', JSON.stringify(metadata));
  fd.append('csv', csvFile);
  const res = await api.post('/bypass/preview', fd, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
}

export interface SubmitArgs {
  metadata: BypassMetadata;
  rows: Array<PreviewRow & { decision: RowDecision }>;
  csvFileName: string;
  csvFileSize: number;
  overridePin?: string;
}

export async function submitBypass(args: SubmitArgs): Promise<{ reportId: string; status: string; action: string }> {
  const res = await api.post(
    '/bypass/submit',
    {
      metadata: args.metadata,
      rows: args.rows,
      csvFileName: args.csvFileName,
      csvFileSize: args.csvFileSize
    }
  );
  return res.data;
}
